/**
 * The Foodpanda tab.
 *
 * WHAT IT IS FOR
 * --------------
 * A Foodpanda order is a sale the shop has made but not been paid for — the
 * customer paid Foodpanda, which pays the shop out two to four weeks later. So
 * every one sits as money owed until the payout lands. This screen is where an
 * owner or manager sees what is still owed and clears an order when Foodpanda
 * has paid it: the money is recognised in the bank and the amount comes off what
 * is outstanding.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatPKR } from '@foodrun/shared';

import { api } from '../../lib/api.js';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { Button } from '../../components/ui/Button.jsx';

const FILTERS = [
  { value: 'outstanding', label: 'Owed' },
  { value: 'settled', label: 'Paid' },
  { value: 'all', label: 'All' },
];

export default function FoodpandaPage() {
  const client = useQueryClient();
  const [filter, setFilter] = useState('outstanding');

  const feed = useQuery({
    queryKey: ['foodpanda', filter],
    queryFn: () => api.get(`/foodpanda?status=${filter}`),
    refetchInterval: 60_000,
  });

  const settle = useMutation({
    mutationFn: (id) => api.post(`/foodpanda/${id}/settle`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['foodpanda'] }),
  });

  const settleAll = useMutation({
    mutationFn: () => api.post('/foodpanda/settle-all'),
    onSuccess: () => client.invalidateQueries({ queryKey: ['foodpanda'] }),
  });

  if (feed.isLoading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Spinner label="Loading Foodpanda orders" />
      </div>
    );
  }

  if (feed.isError || !feed.data) {
    return (
      <p className="py-24 text-center text-sm text-ink-2">
        Could not load Foodpanda orders. Check the branch computer is running.
      </p>
    );
  }

  const { orders, outstanding } = feed.data;
  const busy = settle.isPending || settleAll.isPending;

  return (
    <div className="mx-auto max-w-4xl p-4">
      <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-semibold text-xl text-ink">Foodpanda</h1>
          <p className="mt-0.5 text-sm text-ink-2">
            Sales owed by Foodpanda, cleared when the payout arrives.
          </p>
        </div>

        <div className="text-right">
          <span className="block text-lg font-semibold tabular-nums text-ink">
            {formatPKR(outstanding.total)}
          </span>
          <span className="block text-xs text-ink-3">
            owed across {outstanding.count} order{outstanding.count === 1 ? '' : 's'}
          </span>
        </div>
      </header>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-1.5">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              aria-pressed={filter === f.value}
              onClick={() => setFilter(f.value)}
              className={`h-9 rounded-lg px-3 text-sm font-medium transition-colors ${
                filter === f.value
                  ? 'bg-brand-400 text-surface-2'
                  : 'bg-surface-3 text-ink-2 hover:bg-rule-strong'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* One press to clear a whole settlement, since Foodpanda pays many
            orders together. */}
        {outstanding.count > 0 && (
          <Button
            variant="secondary"
            busy={settleAll.isPending}
            disabled={busy}
            onClick={() => {
              if (
                window.confirm(
                  `Clear all ${outstanding.count} outstanding orders (${formatPKR(outstanding.total)})? Do this when the whole Foodpanda payout has arrived.`
                )
              ) {
                settleAll.mutate();
              }
            }}
          >
            Clear all owed
          </Button>
        )}
      </div>

      {(settle.isError || settleAll.isError) && (
        <p className="mb-3 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">
          {(settle.error ?? settleAll.error)?.message ?? 'That did not work.'}
        </p>
      )}

      {orders.length === 0 ? (
        <p className="rounded-xl border border-rule bg-surface-3 py-12 text-center text-sm text-ink-3">
          {filter === 'outstanding'
            ? 'Nothing owed by Foodpanda right now.'
            : filter === 'settled'
              ? 'No cleared Foodpanda orders yet.'
              : 'No Foodpanda orders yet.'}
        </p>
      ) : (
        <div className="overflow-hidden rounded-xl border border-rule">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-rule bg-surface text-left text-xs text-ink-3">
                <th className="px-3 py-2 font-medium">Order</th>
                <th className="px-3 py-2 font-medium">When</th>
                <th className="px-3 py-2 text-right font-medium">Amount</th>
                <th className="px-3 py-2 text-right font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                const pending = o.settlement_status === 'pending';
                return (
                  <tr key={o.id} className="border-b border-rule last:border-0 bg-surface">
                    <td className="px-3 py-2.5">
                      <span className="tabular-nums text-ink">{o.order_number}</span>
                      {o.customer_name && (
                        <span className="ml-2 text-xs text-ink-3">{o.customer_name}</span>
                      )}
                      {/* The Foodpanda app's own order number, so the tab can be
                          matched to the payout statement. */}
                      {o.foodpanda_order_no && (
                        <span className="mt-0.5 block text-xs text-ink-3">
                          Foodpanda #{o.foodpanda_order_no}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-ink-3">{o.business_date ? dayOf(o.business_date) : whenOf(o.order_date)}</td>
                    <td className="px-3 py-2.5 text-right font-medium tabular-nums text-ink">
                      {formatPKR(o.amount)}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      {pending ? (
                        <Button
                          size="sm"
                          busy={settle.isPending && settle.variables === o.id}
                          disabled={busy}
                          onClick={() => {
                            if (window.confirm(`Clear ${formatPKR(o.amount)} for ${o.order_number}?`)) {
                              settle.mutate(o.id);
                            }
                          }}
                        >
                          Clear
                        </Button>
                      ) : (
                        <span className="text-xs text-good">Paid{whenPaid(o.settlement_date)}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** A trading day, as a date: the day the order counts under. */
function dayOf(ymd) {
  const [y, m, d] = String(ymd).split('-').map(Number);
  return y && m && d ? new Date(y, m - 1, d).toLocaleDateString() : String(ymd);
}

function whenOf(ts) {
  if (!ts) return '—';
  const d = new Date(ts.includes('T') ? ts : ts.replace(' ', 'T') + 'Z');
  return Number.isNaN(d.getTime()) ? String(ts).slice(0, 10) : d.toLocaleDateString();
}

function whenPaid(date) {
  return date ? ` · ${String(date).slice(0, 10)}` : '';
}
